/* tslint:disable:no-input-rename */
import {Directive, ElementRef, EventEmitter, Inject, Input, OnInit, Output} from '@angular/core';
import {RESIZABLE_PARENT} from '@shared/resizable/injection-tokens';
import {ResizableHandleDirective} from '@shared/resizable/resizable-handle.directive';
import {Point} from '@angular/cdk/drag-drop';
import {DOCUMENT} from '@angular/common';
import {clamp, dividePoint, subtractPoints} from '@shared/absolute-drag/helpers';
import {Size} from '@paperlessio/sdk/api/models';

@Directive({
  selector: '[resizable]',
  providers: [{provide: RESIZABLE_PARENT, useExisting: ResizableDirective}],
})
export class ResizableDirective implements OnInit {

  @Input('resizableParentRect') parentRect: DOMRect;
  @Input('resizableScaleFactor') scaleFactor = 1;
  @Input() resizeAxis: 'x' | 'y' | 'both' = 'both';
  @Input() constraintAspectRatio: number; // encoded as 1:x (e.g. 1:2)
  @Input() minSize: Size;
  @Input() maxSize: Size;

  @Output() sizeChanged = new EventEmitter<Size>();
  @Output() resized = new EventEmitter<Size>();

  private handle: ResizableHandleDirective;
  private startPointerPosition: Point;
  private currentPointerPosition: Point;
  private originalClientRect: DOMRect;
  private resizeLimitRect: DOMRect;
  private newDimensions: Size;

  constructor(@Inject(DOCUMENT) private document: Document,
              private element: ElementRef<HTMLElement>) { }

  ngOnInit() {}

  private readonly mouseUp = (event: MouseEvent) => {
    this.removeEventListeners();
    this.resize();
  }

  private readonly mouseMove = (event: MouseEvent) => {
    this.currentPointerPosition = {
      x: event.clientX,
      y: event.clientY
    };

    this.computeDimensions();
  }

  private readonly keyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.sizeChanged.emit(new Size({
        width: this.originalClientRect.width,
        height: this.originalClientRect.height
      }));

      this.removeEventListeners();
      this.reset();
    }
  }

  public registerHandle(handle: ResizableHandleDirective) {
    this.handle = handle;
  }

  public startResize(startPoint: Point) {
    this.startPointerPosition = startPoint;
    this.originalClientRect = this.element.nativeElement.getBoundingClientRect();
    this.addEventListeners();

    let bottomValue = this.parentRect.y + this.parentRect.height;
    bottomValue = this.maxSize?.height ? Math.min(bottomValue, this.originalClientRect.y + this.maxSize.height) : bottomValue;

    let rightValue = this.parentRect.x + this.parentRect.width;
    rightValue = this.maxSize?.width ? Math.min(rightValue, this.originalClientRect.x + this.maxSize.width) : rightValue;

    this.resizeLimitRect = {
      top: this.originalClientRect.y + (this.minSize?.height ?? 0) * this.scaleFactor,
      bottom: bottomValue,
      left: this.originalClientRect.x + (this.minSize?.width ?? 0) * this.scaleFactor,
      right: rightValue
    } as DOMRect;
  }

  private reset() {
    this.newDimensions = this.resizeLimitRect = this.currentPointerPosition = this.startPointerPosition = this.originalClientRect
      = undefined;
  }

  private addEventListeners() {
    this.document.addEventListener('mousemove', this.mouseMove);
    this.document.addEventListener('mouseup', this.mouseUp);
    this.document.addEventListener('keydown', this.keyDown);
  }

  private removeEventListeners() {
    this.document.removeEventListener('mousemove', this.mouseMove);
    this.document.removeEventListener('mouseup', this.mouseUp);
    this.document.removeEventListener('keydown', this.keyDown);
  }

  private constrainPointerPosition(point: Point): Point {
    let {x, y} = point;

    let right = this.resizeLimitRect.right;
    let bottom = this.resizeLimitRect.bottom

    if (this.constraintAspectRatio) {
      const diffX = right - point.x;
      const diffY = bottom - point.y;

      right = Math.min(right, point.x + diffY*this.constraintAspectRatio);
      bottom = Math.min(bottom, point.y + diffX/this.constraintAspectRatio);
    }

    x = clamp(x, this.resizeLimitRect.left, right);
    y = clamp(y, this.resizeLimitRect.top, bottom);

    return {x, y};
  }

  private computeDimensions() {
    if (this.constraintAspectRatio) {
      const diffX = this.currentPointerPosition.x - this.startPointerPosition.x;
      const diffY = this.currentPointerPosition.y - this.startPointerPosition.y;
      const max = Math.max(diffX, diffY);

      this.currentPointerPosition.x = this.startPointerPosition.x + max;
      this.currentPointerPosition.y = this.startPointerPosition.y + max/this.constraintAspectRatio;
    }

    const constrainedDiff = subtractPoints(this.constrainPointerPosition(this.currentPointerPosition), this.startPointerPosition);
    const scaledDiff = dividePoint(constrainedDiff, this.scaleFactor);

    scaledDiff.x += this.originalClientRect.width / this.scaleFactor;
    scaledDiff.y += this.originalClientRect.height / this.scaleFactor;

    if (this.resizeAxis === 'x') {
      scaledDiff.y = 0;
    } else if (this.resizeAxis === 'y') {
      scaledDiff.x = 0;
    }

    this.newDimensions = new Size({ width: scaledDiff.x, height: scaledDiff.y });

    this.sizeChanged.emit(this.newDimensions);
  }

  private resize() {
    if (this.newDimensions) {
      this.resized.emit(this.newDimensions);
    }

    this.reset();
  }
}
